// /crm/prospects/:id/strategy — Strategy Meeting notes form.
//
// Same auto-saving philosophy as Discovery: no modal, no data loss on
// click-off. Mirrors the original HTML form's two-phase structure
// (Pre-Meeting Prep + During-Meeting), opportunity inventory with
// dynamic rows, savings range calc, fee/package selection, and the
// strategy-specific objection handler reference.

import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { usePipeline } from '../../context/PipelineContext';
import { useMeetingNotes } from './useMeetingNotes';
import { STRATEGY_OBJECTIONS } from './meetingData';
import MeetingFormLayout from './MeetingFormLayout';
import { formatCurrency } from '../../utils/formatting';

const DEFAULT_FORM = {
  meetingDate: new Date().toISOString().slice(0, 10),
  // Tax return
  fedLiability: '', stateLiability: '',
  agi: '', effectiveRate: '', marginalRate: '',
  incomeBreakdown: '', returnNotes: '',
  // Opportunities — array of { id, name, amount, confidence }
  opportunities: [{ id: 'opp-init', name: '', amount: '', confidence: 'high' }],
  // Range / fee
  quoteLanguage: '', quotedFee: '', maintenanceFee: '', feeRationale: '',
  selectedTier: null,
  // Package
  packageChoice: null, packageRationale: '',
  // Live meeting
  findingsScript: '', reactionNotes: '',
  meetingOutcome: '',
  closeEngagementSent: false, closeInvoiceSent: false,
  closeFollowupScheduled: false, closeSpouseAligned: false,
  closeNotes: '',
};

const TIERS = [
  { id: 'low',         name: 'Tier 1: Standard',     desc: 'W2 income, simple small business, basic rental. 1-2 strategies in scope.', fee: 'Typical: $3,000 — $5,000' },
  { id: 'medium',      name: 'Tier 2: Moderate',     desc: 'S-Corp with 1-2 rentals, retirement plan optimization, moderate strategies. 3-5 strategies in scope.', fee: 'Typical: $5,000 — $7,500' },
  { id: 'high',        name: 'Tier 3: Complex',      desc: 'Multi-entity structure, advanced strategies (cost seg, PTET, cash balance), real estate professional analysis. 5+ strategies.', fee: 'Typical: $7,500 — $10,000' },
  { id: 'exceptional', name: 'Tier 4: Exceptional',  desc: 'Multi-state, family office complexity, equity events, business sale, ultra high-net-worth. Custom scope.', fee: 'Typical: $10,000+' },
];

const PACKAGES = [
  { id: 'bgw',              label: 'BGW — Bullogic Guided Wealth',          desc: '$650/mo, optional 0.5% AUM, no tax plan' },
  { id: 'btw-standard',     label: 'BTW Standard',                          desc: '$650/mo + 0.5% AUM mandatory + Tax Plan + baked-in Maintain (business owners and rental owners with tax savings)' },
  { id: 'btw-retiree',      label: 'BTW Retiree',                           desc: '$650/mo + 0.5% AUM mandatory, NO Tax Plan fee, NO Maintenance fee (revenue earned through AUM)' },
  { id: 'tax-plan-maintain',label: 'Tax Plan + Maintenance only',           desc: 'one-time Tax Plan + monthly Maintain/Execute/Optimize (BTS-only path, no BWM relationship)' },
];

const OUTCOMES = [
  ['', '— select —'],
  ['signed', 'Signed in meeting / payment processing'],
  ['committed', 'Verbal yes, paperwork pending'],
  ['follow-up', 'Wants to think, follow-up scheduled'],
  ['lost', 'Declined / not a fit'],
  ['other', 'Other (notes below)'],
];

export default function StrategyMeetingView() {
  const { id: prospectId } = useParams();
  const { prospects } = usePipeline();
  const prospect = useMemo(() => (prospects || []).find(p => p.id === prospectId), [prospects, prospectId]);

  const {
    formData, updateField, loaded, savedAt, saving, loadError, saveError,
  } = useMeetingNotes({ prospectId, type: 'strategy', defaultFormData: DEFAULT_FORM });

  // ─── Derived calculations ───
  const calcs = useMemo(() => {
    const fed = parseFloat(formData.fedLiability) || 0;
    const state = parseFloat(formData.stateLiability) || 0;
    const totalLiability = fed + state;
    const opps = formData.opportunities || [];
    const sumByConf = (conf) => opps
      .filter(o => o.confidence === conf)
      .reduce((s, o) => s + (parseFloat(o.amount) || 0), 0);
    const high = sumByConf('high');
    const med = sumByConf('medium');
    const low = sumByConf('low');
    const total = high + med + low;
    const rangeLow = high * 0.85;
    const rangeMid = high + med;
    const rangeHigh = total;
    const lowPct = totalLiability > 0 ? (rangeLow / totalLiability * 100) : null;
    const highPct = totalLiability > 0 ? (rangeHigh / totalLiability * 100) : null;

    let warning = null;
    if (totalLiability > 0 && rangeHigh > totalLiability) {
      warning = `Identified savings exceed total liability. This is a Danny-level red flag. Re-check the math.`;
    } else if (totalLiability > 0 && rangeHigh > totalLiability * 0.6) {
      warning = `High-end estimate is ${(rangeHigh/totalLiability*100).toFixed(0)}% of last year's tax liability. Savings above ~50% of liability are rare and usually require special circumstances. Re-check your opportunity dollar estimates and confidence ratings before quoting.`;
    }

    const taxPlanFee = parseFloat(formData.quotedFee) || 0;
    const maintainCalc = taxPlanFee > 0 ? Math.max(150, (taxPlanFee * 0.30) / 12) : 0;

    return { totalLiability, high, med, low, total, rangeLow, rangeMid, rangeHigh, lowPct, highPct, warning, maintainCalc };
  }, [formData.fedLiability, formData.stateLiability, formData.opportunities, formData.quotedFee]);

  // Opportunity row helpers
  function addOpp() {
    const next = [...(formData.opportunities || []), {
      id: 'opp-' + Date.now() + '-' + Math.random().toString(36).slice(2, 7),
      name: '', amount: '', confidence: 'high',
    }];
    updateField('opportunities', next);
  }
  function removeOpp(id) {
    updateField('opportunities', (formData.opportunities || []).filter(o => o.id !== id));
  }
  function updateOpp(id, field, value) {
    updateField('opportunities',
      (formData.opportunities || []).map(o => o.id === id ? { ...o, [field]: value } : o)
    );
  }

  return (
    <MeetingFormLayout
      title="Strategy Meeting"
      prospect={prospect}
      savedAt={savedAt}
      saving={saving}
      loadError={loadError}
      saveError={saveError}
    >
      {!loaded ? (
        <div className="text-[12px] text-[#888] p-4">Loading saved notes…</div>
      ) : (
        <>
          {/* Date */}
          <Card>
            <Label>Meeting date</Label>
            <input
              type="date"
              value={formData.meetingDate || ''}
              onChange={e => updateField('meetingDate', e.target.value)}
              className={inputCls + ' max-w-[180px]'}
            />
          </Card>

          {/* Phase 1 banner */}
          <PhaseBanner phase="prep" label="Phase 1">Pre-Meeting Prep — complete BEFORE the meeting starts</PhaseBanner>

          {/* Path Check */}
          <Card>
            <h2 className="text-[14px] font-bold text-navy mb-2">Path Check: Does this prospect have a meaningful tax plan opportunity?</h2>
            <Note>Some BWM-fit prospects come to Strategy without a real tax plan opportunity. Most common: one long-term rental, no business, no advanced strategies in scope. They fit the ICP, but a tax plan would be padded and indefensible.</Note>
            <Note><strong>If yes (tax plan opportunity exists):</strong> Run the full Phase 1 prep below. Standard flow.</Note>
            <Note><strong>If no (no tax plan opportunity):</strong> Skip Sections 1-4 of Phase 1. Go directly to Section 5 and select BGW. Do not invent strategies to justify a tax plan, that's worse than no tax plan at all.</Note>
          </Card>

          {/* Three Rules */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-[6px] p-4">
            <h3 className="text-[12px] font-bold uppercase tracking-wider text-yellow-900 mb-2">The Three Rules That Prevent Disasters</h3>
            <ol className="space-y-1.5 text-[12px] list-none pl-0">
              <Rule num="1"><strong>Liability gets calculated and written down before the meeting starts.</strong> No exceptions. Skipping this is what blew up Danny.</Rule>
              <Rule num="2"><strong>Savings range is tied to math, not promises.</strong> Each dollar of estimated savings traces back to a specific strategy on the worksheet. If you can't show the math, don't quote the number.</Rule>
              <Rule num="3"><strong>Fee is anchored to scope, not savings.</strong> Never use "10% of savings" or any multiple of savings to set the fee. Quote the fee independently based on complexity.</Rule>
            </ol>
          </div>

          {/* Section 1 — Tax Return */}
          <Segment num="1" title="Tax Return Analysis" prep>
            <Note>Pull these numbers from their tax return before the meeting. The numbers anchor everything that follows.</Note>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <NumField label="Federal liability ($)" value={formData.fedLiability} onChange={v => updateField('fedLiability', v)} />
              <NumField label="State liability ($)" value={formData.stateLiability} onChange={v => updateField('stateLiability', v)} />
            </div>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <NumField label="AGI ($)" value={formData.agi} onChange={v => updateField('agi', v)} />
              <NumField label="Effective rate (%)" value={formData.effectiveRate} onChange={v => updateField('effectiveRate', v)} step="0.1" />
              <NumField label="Marginal bracket (%)" value={formData.marginalRate} onChange={v => updateField('marginalRate', v)} step="1" />
            </div>
            <Field label="Income types and amounts" value={formData.incomeBreakdown} onChange={v => updateField('incomeBreakdown', v)} placeholder="W2: $X | Business: $X | Rental: $X | etc." />
            <Field label="Notable items spotted on the return" value={formData.returnNotes} onChange={v => updateField('returnNotes', v)} minHeight={80} placeholder="e.g., depreciation not taken on rental property. Three rentals reported as one. SEP IRA undercontributed." />
          </Segment>

          {/* Section 2 — Opportunities */}
          <Segment num="2" title="Opportunity Inventory" prep>
            <Note>List every strategy you identified during your analysis. Each one needs a specific dollar estimate and a confidence rating.</Note>
            <div className="grid grid-cols-[2.2fr_1fr_1fr_auto] gap-2 text-[10px] font-bold uppercase tracking-wider text-[#888] mb-1.5 px-1">
              <div>Strategy</div><div>Annual Savings ($)</div><div>Confidence</div><div className="w-16">{' '}</div>
            </div>
            <div className="space-y-1.5 mb-2">
              {(formData.opportunities || []).map(opp => (
                <div key={opp.id} className="grid grid-cols-[2.2fr_1fr_1fr_auto] gap-2 items-start">
                  <input type="text" placeholder="Strategy name" value={opp.name} onChange={e => updateOpp(opp.id, 'name', e.target.value)} className={inputCls} />
                  <input type="number" placeholder="$" value={opp.amount} onChange={e => updateOpp(opp.id, 'amount', e.target.value)} className={inputCls} />
                  <select value={opp.confidence} onChange={e => updateOpp(opp.id, 'confidence', e.target.value)} className={inputCls}>
                    <option value="high">High</option>
                    <option value="medium">Medium</option>
                    <option value="low">Low</option>
                  </select>
                  <button onClick={() => removeOpp(opp.id)} className="px-2 text-[12px] font-bold text-rose border border-rose rounded-[3px] hover:bg-rose hover:text-white w-12">×</button>
                </div>
              ))}
            </div>
            <button onClick={addOpp} className="w-full p-2 text-[12px] border border-dashed border-gray-400 text-[#666] rounded-[3px] hover:border-navy hover:text-navy">+ Add opportunity</button>

            <CalcOutput title="Opportunity Summary">
              <CalcRow label="High-confidence savings (defensible)" value={formatCurrency(calcs.high)} />
              <CalcRow label="Medium-confidence savings" value={formatCurrency(calcs.med)} />
              <CalcRow label="Low-confidence savings (exploratory)" value={formatCurrency(calcs.low)} />
              <CalcRow label="Total identified savings" value={formatCurrency(calcs.total)} bold />
            </CalcOutput>
          </Segment>

          {/* Section 3 — Realistic Range */}
          <Segment num="3" title="Realistic Savings Range" prep>
            <Note>The Low number is what you can almost certainly deliver. The High number is the optimistic ceiling. NEVER quote outside this range.</Note>
            <CalcOutput title="Defensible Range">
              <CalcRow label="Total liability (last year)" value={formatCurrency(calcs.totalLiability)} />
              <CalcRow label="Low estimate (high-confidence × 0.85)" value={formatCurrency(calcs.rangeLow)} large />
              <CalcRow label="Mid estimate (high + medium)" value={formatCurrency(calcs.rangeMid)} large />
              <CalcRow label="High estimate (all identified)" value={formatCurrency(calcs.rangeHigh)} large />
              <CalcRow
                label="Range as % of liability"
                value={calcs.lowPct !== null ? `${calcs.lowPct.toFixed(0)}% — ${calcs.highPct.toFixed(0)}%` : '— %'}
              />
            </CalcOutput>
            {calcs.warning && (
              <div className="bg-amber-50 border border-amber-300 text-amber-900 p-3 rounded-[3px] text-[12px] mt-2">
                <strong className="block mb-1">Realism check failed.</strong>
                {calcs.warning}
              </div>
            )}
            <Field label="What you'll quote in the meeting (savings language)" value={formData.quoteLanguage} onChange={v => updateField('quoteLanguage', v)} placeholder="e.g., Based on the analysis, we identified $X to $Y in annual savings opportunities." minHeight={80} />
          </Segment>

          {/* Section 4 — Fee */}
          <Segment num="4" title="Fee Anchoring (Independent of Savings)" prep>
            <Note>Pick the complexity tier that matches this engagement. The fee is tied to the WORK we're doing, not a percentage of savings.</Note>
            <div className="space-y-2 mb-3">
              {TIERS.map(tier => (
                <button
                  key={tier.id}
                  onClick={() => updateField('selectedTier', tier.id)}
                  className={`w-full text-left p-3 rounded-[4px] border transition-all ${
                    formData.selectedTier === tier.id
                      ? 'border-bullogic-blue border-2 bg-bone/40'
                      : 'border-gray-300 hover:border-gray-500'
                  }`}
                >
                  <div className="text-[13px] font-bold text-navy">{tier.name}</div>
                  <div className="text-[12px] text-[#666] mt-0.5">{tier.desc}</div>
                  <div className="text-[12px] font-mono text-deep-teal font-bold mt-1">{tier.fee}</div>
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <NumField label="Fee you'll quote ($)" value={formData.quotedFee} onChange={v => updateField('quotedFee', v)} />
              <NumField label="Maintenance fee (monthly)" value={formData.maintenanceFee} onChange={v => updateField('maintenanceFee', v)} />
            </div>
            <Field label="Why this fee for this scope (justification you can defend if asked)" value={formData.feeRationale} onChange={v => updateField('feeRationale', v)} minHeight={80} />
          </Segment>

          {/* Section 5 — Package */}
          <Segment num="5" title="Package Recommendation" prep>
            <Note>Pick the right path based on prospect type and whether a tax plan opportunity exists.</Note>
            <div className="space-y-2 mb-3">
              {PACKAGES.map(pkg => (
                <label key={pkg.id} className="flex items-start gap-2 text-[12px] p-2 border border-gray-200 rounded-[3px] hover:bg-bone/30 cursor-pointer">
                  <input
                    type="radio"
                    name="package"
                    value={pkg.id}
                    checked={formData.packageChoice === pkg.id}
                    onChange={() => updateField('packageChoice', pkg.id)}
                    className="mt-1"
                  />
                  <div>
                    <div className="font-bold text-navy">{pkg.label}</div>
                    <div className="text-[#666]">{pkg.desc}</div>
                  </div>
                </label>
              ))}
            </div>
            <CalcOutput title="BTW Maintain monthly fee (auto-calc reference)">
              <CalcRow label="Formula: max($150, Tax Plan fee × 0.30 / 12)" value={calcs.maintainCalc > 0 ? `${formatCurrency(calcs.maintainCalc)}/mo` : '$0/mo'} />
              <CalcRow label="Note: baked into BTW monthly. Quote BTW as all-in." value="" />
            </CalcOutput>
            <Field label="Why this package fits this prospect" value={formData.packageRationale} onChange={v => updateField('packageRationale', v)} minHeight={80} />
          </Segment>

          {/* Phase 2 banner */}
          <PhaseBanner phase="live" label="Phase 2">During-Meeting Structure — work this in order during the call</PhaseBanner>

          {/* Section A — Recap */}
          <Segment letter="A" title="Recap and Frame" duration="3 min" live>
            <Note>Open the meeting by anchoring back to Discovery. Set the agenda.</Note>
            <Script>"Thanks for getting back together. Quick recap of where we left off in Discovery. We surfaced [the 2-3 things you disturbed them on]. You sent me the tax return, I've spent time on it, and today I'm going to walk you through what I found. Three things on the agenda. First, what's actually happening on your tax return right now. Second, the specific opportunities I see and what they're worth in real dollars. Third, what working together looks like and what it costs. Sound good?"</Script>
          </Segment>

          {/* Section B — Walk Analysis */}
          <Segment letter="B" title="Walk Through the Analysis" duration="10-15 min" live>
            <Note>This is where you create the wound. Show them what's actually on their return. Numbers, not adjectives. Don't pile on. Let each finding land.</Note>
            <Script>"Let me start with what's currently happening. [Walk through 2-4 specific findings from the return notes section above. Frame each as 'this is what's happening and here's what it means.' Pause after each.]"</Script>
            <Field label="Specific findings to walk through (pulled from prep)" value={formData.findingsScript} onChange={v => updateField('findingsScript', v)} minHeight={140} placeholder="Findings to cover, in order. Pull from your prep section." />
          </Segment>

          {/* Section C — Present Range */}
          <Segment letter="C" title="Present the Opportunities and Range" duration="5-10 min" live>
            <Note>Lead with high-confidence numbers. Be explicit about confidence. Quote the range from your prep section, not a number you make up on the spot.</Note>
            <div className="bg-yellow-50 border-2 border-yellow-300 rounded-[6px] p-3 mb-3">
              <h3 className="text-[11px] font-bold uppercase tracking-wider text-yellow-900 mb-2">Watch yourself here</h3>
              <ol className="space-y-1.5 text-[12px] list-none pl-0">
                <Rule num="!"><strong>Quote only what's on your prep worksheet.</strong> If a strategy isn't in your opportunity inventory, it isn't part of today's quote.</Rule>
                <Rule num="!"><strong>If they push you to "guarantee" a number, don't.</strong> Answer with confidence levels.</Rule>
              </ol>
            </div>
            <Script>"Based on the analysis, here's what I see. [Walk through opportunities by confidence tier. Lead with the high-confidence number, not the high-end number.]
            {'\n\n'}
            On the high-confidence side, [list specific high-conf strategies with dollar amounts]. That's around $X annually, and these are strategies I can show you the math on right now from your return.
            {'\n\n'}
            On top of that, there are medium-confidence opportunities that depend on implementation choices we'd make together. [List]. Those add another $Y to $Z.
            {'\n\n'}
            The total range we're looking at is $LOW to $HIGH per year, with $LOW being what I'd commit to as the floor."</Script>
            <Field label="Notes during this segment (what they responded to, what landed)" value={formData.reactionNotes} onChange={v => updateField('reactionNotes', v)} minHeight={100} />
          </Segment>

          {/* Section D — Position Package */}
          <Segment letter="D" title="Position the Package" duration="5 min" live>
            <Note>Sell the relationship, not just the tax plan.</Note>
            <Script>"Here's what working together looks like. [Use your package recommendation rationale from the prep section.]
            {'\n\n'}
            The Tax Plan we just walked through is the entry point. From there, we're managing the implementation throughout the year, doing biannual planning meetings, coordinating across your other professionals, and watching for changes in your situation. That's [BGW or BTW]. The Tax Plan itself is a one-time deliverable. The relationship is what actually delivers the savings year after year."</Script>
          </Segment>

          {/* Section E — Quote & Close */}
          <Segment letter="E" title="Quote the Fee and Close" duration="5-10 min" live>
            <Note>Quote the fee from your prep section. Anchor it to scope. Then close to commitments.</Note>
            <Script>"For the Tax Plan and implementation, the fee is $[FROM PREP]. That covers the full analysis, the strategies we walked through, the implementation roadmap, and our work through the end of the year. [If applicable: The ongoing wealth management relationship is $[X]/month plus [Y]% AUM if we manage assets.]
            {'\n\n'}
            We charge 50% upfront to start the engagement. The remaining 50% is invoiced when we deliver the final plan."</Script>
            <SubBold>The close (don't skip this):</SubBold>
            <Script>"Are you ready to move forward today?"</Script>

            <Label>Strategy Meeting outcome</Label>
            <select
              value={formData.meetingOutcome || ''}
              onChange={e => updateField('meetingOutcome', e.target.value)}
              className={inputCls}
            >
              {OUTCOMES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>

            <div className="space-y-1.5 mt-3">
              {[
                ['closeEngagementSent', 'Engagement letter sent'],
                ['closeInvoiceSent', '50% invoice sent'],
                ['closeFollowupScheduled', 'Follow-up scheduled (if not closed)'],
                ['closeSpouseAligned', 'Spouse aligned with decision'],
              ].map(([key, label]) => (
                <label key={key} className="flex items-center gap-2 text-[12px]">
                  <input type="checkbox" checked={!!formData[key]} onChange={() => updateField(key, !formData[key])} />
                  <span>{label}</span>
                </label>
              ))}
            </div>
            <Field label="Notes, follow-up actions, what they pushed back on" value={formData.closeNotes} onChange={v => updateField('closeNotes', v)} minHeight={100} />
          </Segment>

          {/* Section F — Objection Handlers */}
          <Segment letter="F" title="Strategy-Specific Objection Handlers" duration="reference" live>
            <Note>Objections that come up in Strategy specifically. These are different from Discovery objections because they show up after the prospect has seen real numbers.</Note>
            {STRATEGY_OBJECTIONS.map((obj, i) => (
              <details key={i} className="border border-gray-200 rounded-[4px] mb-2 group">
                <summary className="cursor-pointer p-3 text-[13px] font-bold list-none flex items-center gap-2 hover:bg-bone/30">
                  <span className="text-bullogic-blue text-[14px] group-open:rotate-45 transition-transform">+</span>
                  <span>{obj.title}</span>
                </summary>
                <div className="p-3 pt-1 border-t border-gray-100">
                  <div className="text-[11px] text-[#777] italic mb-2">When it comes: {obj.when}</div>
                  <div className="text-[12px] mb-2 p-2 bg-gray-50 rounded border-l-2 border-gray-400">
                    <strong>Principle:</strong> {obj.principle}
                  </div>
                  <div className="bg-bone/40 border-l-[3px] border-bullogic-blue p-4 italic text-[16px] whitespace-pre-wrap leading-[1.65]">
                    {obj.script}
                  </div>
                </div>
              </details>
            ))}
          </Segment>
        </>
      )}
    </MeetingFormLayout>
  );
}

// ─── Reusable bits ───────────────────────────────────────────────
const inputCls = 'w-full px-3 py-2 text-[13px] border border-gray-300 rounded-[3px] focus:outline-none focus:border-bullogic-blue';

function Card({ children }) {
  return <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">{children}</div>;
}
function PhaseBanner({ phase, label, children }) {
  const cls = phase === 'prep'
    ? 'bg-slate-700 text-white'
    : 'bg-emerald-900 text-white';
  return (
    <div className={`${cls} rounded-[6px] p-3 text-[13px] font-bold flex items-center gap-2`}>
      <span className="text-[10px] uppercase tracking-wider bg-white/20 px-2 py-0.5 rounded-full">{label}</span>
      <span>{children}</span>
    </div>
  );
}
function Segment({ num, letter, title, duration, children, prep, live }) {
  const idCls = prep ? 'bg-slate-700' : live ? 'bg-emerald-900' : 'bg-navy';
  return (
    <div className="bg-white rounded-[4px] shadow-[0_2px_6px_rgba(0,0,0,0.07)] p-5">
      <div className="flex items-center gap-2 mb-3">
        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full ${idCls} text-white text-[11px] font-bold`}>{num || letter}</span>
        <h2 className="text-[15px] font-bold text-navy flex-1">{title}</h2>
        {duration && <span className="text-[10px] text-[#888]">{duration}</span>}
      </div>
      {children}
    </div>
  );
}
function Rule({ num, children }) {
  return (
    <li className="pl-7 relative leading-relaxed">
      <span className="absolute left-0 top-0 bg-yellow-700 text-white w-5 h-5 rounded-full text-center text-[10px] font-bold leading-5">{num}</span>
      {children}
    </li>
  );
}
function Note({ children }) { return <p className="text-[12px] text-[#666] mb-2">{children}</p>; }
function SubBold({ children }) { return <div className="text-[12px] font-bold text-navy mt-3 mb-1">{children}</div>; }
function Script({ children }) {
  // Scripts are read live during calls — larger font + relaxed leading
  // makes them scannable without leaning toward the screen.
  return (
    <div className="bg-bone/40 border-l-[3px] border-bullogic-blue p-4 my-2 italic text-[16px] whitespace-pre-wrap leading-[1.65] rounded-r-[3px]">
      {children}
    </div>
  );
}
function Label({ children }) { return <label className="block text-[11px] font-bold uppercase tracking-wider text-navy mt-3 mb-1">{children}</label>; }
function Field({ label, value, onChange, minHeight = 56, placeholder }) {
  return (
    <>
      <Label>{label}</Label>
      <textarea
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={inputCls}
        style={{ minHeight, resize: 'vertical' }}
      />
    </>
  );
}
function NumField({ label, value, onChange, step = '1' }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type="number"
        step={step}
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        className={inputCls}
      />
    </div>
  );
}
function CalcOutput({ title, children }) {
  return (
    <div className="bg-bone/40 rounded-[4px] p-3 my-3">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-[#666] mb-2">{title}</h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}
function CalcRow({ label, value, large, bold }) {
  return (
    <div className="flex justify-between items-center text-[12px] py-1 border-b border-gray-200/60 last:border-b-0">
      <span className="text-[#555]">{label}</span>
      <span className={`font-mono tabular-nums ${large ? 'text-[15px] text-deep-teal' : ''} ${bold ? 'font-bold text-navy' : 'font-bold text-navy'}`}>{value}</span>
    </div>
  );
}
